import CorrectionWizard from './pages/company/corrections/CorrectionWizard'
import CorrectionDetail from './pages/company/corrections/CorrectionDetail'
import CanonicalEntityRoute from './navigation/CanonicalEntityRoute'
import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom'
import Login from './pages/Login'
import AdminUsersList from './pages/admin/UsersList'
import AdminUserFormPage from './pages/admin/UserFormPage'
import AdminDeletedRecords from './pages/admin/DeletedRecords'
import AdminRoles from './pages/admin/Roles'
import SystemParams from './pages/admin/SystemParams'
import BotResponseRules from './pages/admin/BotResponseRules'
import AdminReference from './pages/admin/Reference'
import CustomFields from './pages/admin/CustomFields'
import BudgetEmployees from './pages/admin/BudgetEmployees'
import CustomTeamPercentages from './pages/admin/CustomTeamPercentages'
import GipRules from './pages/admin/GipRules'
import BudgetRules from './pages/admin/BudgetRules'
import BudgetRuleTypeForm from './pages/admin/BudgetRuleTypeForm'
import Team from './pages/company/Team'
import EmployeeCard from './pages/company/EmployeeCard'
import EmployeeEdit from './pages/company/EmployeeEdit'
import Departments from './pages/company/Departments'
import DepartmentCard from './pages/company/DepartmentCard'
import Clients from './pages/company/Clients'
import ClientCard from './pages/company/ClientCard'
import ClientEdit from './pages/company/ClientEdit'
import ContactEdit from './pages/company/ContactEdit'
import Objects from './pages/company/Objects'
import ObjectCard from './pages/company/ObjectCard'
import ObjectEdit from './pages/company/ObjectEdit'
import Budgets from './pages/company/Budgets'
import BudgetCard from './pages/company/BudgetCard'
import ObjectStageForm from './pages/company/ObjectStageForm'
import Contracts from './pages/company/Contracts'
import Stages from './pages/company/Stages'
import ContractCard from './pages/company/ContractCard'
import ContractApprovals from './pages/company/ContractApprovals'
import ContractReportPreview from './pages/company/ContractReportPreview'
import StageReportPreview from './pages/company/StageReportPreview'
import StageIncomeReportPreview from './pages/company/StageIncomeReportPreview'
import SummaryActsReportPreview from './pages/company/SummaryActsReportPreview'
import ReportsList from './pages/company/reports/ReportsList'
import ReportDetailRoute from './pages/company/reports/ReportDetailRoute'
import IncomeReportPage from './pages/company/reports/IncomeReportPage'
import IncomeReportConfigPage from './pages/company/reports/IncomeReportConfigPage'
import IncomeReportAccountantPage from './pages/company/reports/IncomeReportAccountantPage'
import ContractEdit from './pages/company/ContractEdit'
import StageCard from './pages/company/StageCard'
import StageAllocationEdit from './pages/company/StageAllocationEdit'
import StageEdit from './pages/company/StageEdit'
import ActEdit from './pages/company/ActEdit'
import Acts from './pages/company/Acts'
import ActCard from './pages/company/ActCard'
import Settings from './pages/Settings'
import AiPage from './pages/AiPage'
import NotFound from './pages/NotFound'
import AccessDenied from './pages/AccessDenied'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import Layout from './components/Layout'
import ErrorBoundary from './components/ErrorBoundary'
import { canAccessRoute } from './utils/routeAccess'
import './App.css'
import { NavigationScroll } from './navigation/navigation'

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth()
  const location = useLocation()

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />
  }

  if (!canAccessRoute(location.pathname, user)) {
    return <AccessDenied />
  }

  return children
}

function HomeRedirect() {
  const { user } = useAuth()
  if (user?.employee_full_name) {
    return <Navigate to={`/company/team/${encodeURIComponent(user.employee_id || user.employee_full_name)}`} replace />
  }
  return <Navigate to="/company/team" replace />
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forbidden" element={
        <ProtectedRoute>
          <AccessDenied />
        </ProtectedRoute>
      } />
      <Route path="/" element={
        <ProtectedRoute>
          <HomeRedirect />
        </ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute>
          <Layout>
            <Outlet />
          </Layout>
        </ProtectedRoute>
      }>
        <Route index element={<AdminUsersList />} />
        <Route path="new" element={<AdminUserFormPage />} />
        <Route path=":id/edit" element={<AdminUserFormPage />} />
      </Route>
      <Route path="/admin/deleted-records" element={
        <ProtectedRoute>
          <Layout>
            <AdminDeletedRecords />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/roles" element={
        <ProtectedRoute>
          <Layout>
            <AdminRoles />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/system-params" element={
        <ProtectedRoute>
          <Layout>
            <SystemParams />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/bot-response-rules" element={
        <ProtectedRoute>
          <Layout>
            <BotResponseRules />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/reference" element={
        <ProtectedRoute>
          <Layout>
            <AdminReference />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/custom-fields" element={
        <ProtectedRoute>
          <Layout>
            <CustomFields />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/budget/employees" element={
        <ProtectedRoute>
          <Layout>
            <BudgetEmployees />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/budget/custom-team-percentages" element={
        <ProtectedRoute>
          <Layout>
            <CustomTeamPercentages />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/budget/gip-rules" element={
        <ProtectedRoute>
          <Layout>
            <GipRules />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/budget/budget-rules" element={
        <ProtectedRoute>
          <Layout>
            <BudgetRules />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/budget/budget-rules/new" element={
        <ProtectedRoute>
          <Layout>
            <BudgetRuleTypeForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/budget/budget-rules/:distributionType/edit" element={
        <ProtectedRoute>
          <Layout>
            <BudgetRuleTypeForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/team" element={
        <ProtectedRoute>
          <Layout>
            <Team />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/team/income-report" element={
        <ProtectedRoute>
          <Layout>
            <Navigate to="/company/reports" replace />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/team/new" element={
        <ProtectedRoute>
          <Layout>
            <EmployeeEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/team/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <EmployeeEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/team/:name" element={
        <ProtectedRoute>
          <Layout>
            <EmployeeCard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/departments" element={
        <ProtectedRoute>
          <Layout>
            <Departments />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/departments/:id" element={
        <ProtectedRoute>
          <Layout>
            <DepartmentCard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/clients" element={
        <ProtectedRoute>
          <Layout>
            <Clients />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/clients/new" element={
        <ProtectedRoute>
          <Layout>
            <ClientEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/clients/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <ClientEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/clients/:clientId/contacts/new" element={
        <ProtectedRoute>
          <Layout>
            <ContactEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/clients/:clientId/contacts/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <ContactEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/client/:id" element={
        <ProtectedRoute>
          <Layout>
            <ClientCard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/objects" element={
        <ProtectedRoute>
          <Layout>
            <Objects />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/objects/new" element={
        <ProtectedRoute>
          <Layout>
            <ObjectEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/objects/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <ObjectEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/object/:id" element={
        <ProtectedRoute>
          <Layout>
            <ObjectCard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/budgets" element={
        <ProtectedRoute>
          <Layout>
            <Budgets />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/budgets/:id/corrections/new" element={<ProtectedRoute><Layout><CorrectionWizard /></Layout></ProtectedRoute>} />
      <Route path="/company/corrections/:correctionId/edit" element={<ProtectedRoute><Layout><CorrectionWizard /></Layout></ProtectedRoute>} />
      <Route path="/company/corrections/:correctionId" element={<ProtectedRoute><Layout><CorrectionDetail /></Layout></ProtectedRoute>} />
      <Route path="/company/budgets/new" element={
        <ProtectedRoute>
          <Layout>
            <ObjectStageForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/budgets/:stageId/edit" element={
        <ProtectedRoute>
          <Layout>
            <ObjectStageForm />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/budgets/:id" element={
        <ProtectedRoute>
          <Layout>
            <BudgetCard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/budgets/:budgetId/approvals" element={
        <ProtectedRoute>
          <Layout>
            <ContractApprovals />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/contracts" element={
        <ProtectedRoute>
          <Layout>
            <Contracts />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/stages" element={
        <ProtectedRoute>
          <Layout>
            <Stages />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/stages/summary-report" element={
        <ProtectedRoute>
          <Layout>
            <SummaryActsReportPreview />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/contracts/new" element={
        <ProtectedRoute>
          <Layout>
            <ContractEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/contracts/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <ContractEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/contracts/:contractId/stages/new" element={
        <ProtectedRoute>
          <Layout>
            <CanonicalEntityRoute><StageEdit /></CanonicalEntityRoute>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/contracts/:contractId/stages/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <CanonicalEntityRoute><StageEdit /></CanonicalEntityRoute>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/contracts/:contractId/acts/new" element={
        <ProtectedRoute>
          <Layout>
            <ActEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/contracts/:contractId/acts/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <ActEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/contract/:id" element={
        <ProtectedRoute>
          <Layout>
            <ContractCard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/stage/:id" element={
        <ProtectedRoute>
          <Layout>
            <CanonicalEntityRoute><StageCard /></CanonicalEntityRoute>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/stage/:id/allocation-edit" element={
        <ProtectedRoute>
          <Layout>
            <CanonicalEntityRoute><StageAllocationEdit /></CanonicalEntityRoute>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/contract/:id/approvals" element={
        <ProtectedRoute>
          <Layout>
            <ContractApprovals />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/contract/:id/report-preview" element={
        <ProtectedRoute>
          <Layout>
            <ContractReportPreview />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/stage/:id/report-preview" element={
        <ProtectedRoute>
          <Layout>
            <CanonicalEntityRoute><StageReportPreview /></CanonicalEntityRoute>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/stage/:id/income-report" element={
        <ProtectedRoute>
          <Layout>
            <CanonicalEntityRoute><StageIncomeReportPreview /></CanonicalEntityRoute>
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/acts" element={
        <ProtectedRoute>
          <Layout>
            <Acts />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/reports" element={
        <ProtectedRoute>
          <Layout>
            <ReportsList />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/reports/:id" element={
        <ProtectedRoute>
          <Layout>
            <ReportDetailRoute />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/reports/:id/config" element={
        <ProtectedRoute>
          <Layout>
            <IncomeReportConfigPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/reports/:id/accountant" element={
        <ProtectedRoute>
          <Layout>
            <IncomeReportAccountantPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/company/acts/:id/edit" element={
        <ProtectedRoute>
          <Layout>
            <ActEdit />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/act/:id" element={
        <ProtectedRoute>
          <Layout>
            <ActCard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <Settings />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/ai" element={
        <ProtectedRoute>
          <Layout>
            <AiPage />
          </Layout>
        </ProtectedRoute>
      } />
      {/* Nginx редиректит на /ai/ — отдельный маршрут, иначе SPA может попасть в NotFound */}
      <Route path="/ai/" element={
        <ProtectedRoute>
          <Layout>
            <AiPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <NavigationScroll />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  )
}

export default App
